'use client';
import { createContext, ReactNode, useCallback, useEffect, useState } from "react";
import { useContextStrict } from "@web-libs/core/context/context.functions";
import { useHttpContext } from "@web-libs/core/http/infrastructure/client-side/http.context";

export interface UnreadCounts {
  countChatMessages: number;
  countInformations: number;
  countNews: number;
  countEvents: number;
}

interface UnreadCountsContextType {
  unreadCounts: UnreadCounts;
  updateUnreadCounts: (newCounts: Partial<UnreadCounts>) => void;
  fetchUnreadCounts: () => Promise<void>;
}

export const UnreadCountsContext = createContext<UnreadCountsContextType | null>(null);

UnreadCountsContext.displayName = 'UnreadCountsContext';

export const useUnreadCounts = () => useContextStrict(UnreadCountsContext);

export const UnreadCountsProvider = ({ children }: { children: ReactNode }) => {

  const [unreadCounts, setUnreadCounts] = useState<UnreadCounts>({
    countChatMessages: 0,
    countInformations: 0,
    countNews: 0,
    countEvents: 0,
  });

  let httpConcern;
  try {
    httpConcern = useHttpContext();
  } catch (error) {
    httpConcern = null;
  }

  const fetchUnreadCounts = useCallback(async () => {
    if (!httpConcern) {
      return;
    }
    try {
      const response = await httpConcern.get('/general/unread');
      setUnreadCounts(response);
    } catch (error) {
    }
  }, [httpConcern]);

  const updateUnreadCounts = useCallback((newCounts: Partial<UnreadCounts>) => {
    setUnreadCounts(prevCounts => ({
      ...prevCounts,
      ...newCounts
    }));
  }, []);

  useEffect(() => {
    if (fetchUnreadCounts) {
      fetchUnreadCounts();
    }
  }, [fetchUnreadCounts]);


  return (
    <UnreadCountsContext.Provider
      value={{
        unreadCounts,
        updateUnreadCounts,
        fetchUnreadCounts
      }}
    >
      {children}
    </UnreadCountsContext.Provider>
  );
};
